import localforage from 'localforage'
import { storeToRefs } from 'pinia'

import type { NavigationGuard } from 'vue-router'

import { constants } from '@/globals'
import { UserType } from '@/api'
import { useAuthenticationStore, useCustomerStore } from '@/shared/authentication'
import { useUserStore } from '@/shared/authorization'
import { useCustomerId } from '@/shared/composables/useCustomerId.ts'
import { useTenantScopesStore } from '@/apps/service/pages/api/stores/TenantScopesStore'

/* This is primarily used as a beforeEach hook on the router. */
export function useAuthenticationGuard(): NavigationGuard {
  const redirectValueKey = constants.LOCAL_STORAGE_AUTHENTICATION_REDIRECT

  return async (to, from) => {
    if (to.path === from.path) {
      /* This is a workaround to handle the case where the user is
         already on the target route, but parameters might have changed,
         as is the case if the user uses the 'Used by X Modules' button
         in Device and System Type pages which navigates to Modules but
         with a filter. If the user then clicks on the Home breadcrumb
         or Customer name breadcrumb, which both navigate to Modules too
         as that's the landing page, it then causes the router to stall
         (it's already at the target) and a progress spinner bar by our
         code is shown which never goes away.  In this case we detect
         this (paths would be the same, but params different) and
         reload the whole page, which clears the filters (wipes params).
         A not-so-eligant workaround but it works, and this is a very
         rare use case anway.
                                                - Christian Ruocco */
      window.location.reload()
      return false
    }

    const { checkOAuthSession, tokenExpirationTimestamp } = useAuthenticationStore()
    const userStore = useUserStore()
    const { getMe, hasScope, hasMinType } = userStore
    const { user } = storeToRefs(userStore)
    const { setActiveCustomer, clearActiveCustomer } = useCustomerStore()
    /* Acquire any preauthentication path+params value that might be stored in
       persistent local storage by authenticationStore.ts before jumping to
       Keycloak login.  */
    const preAuthRedirect = await localforage.getItem<string>(redirectValueKey)

    // if (preAuthRedirect) console.log("authenticationGuard: got redirect value: " + preAuthRedirect) // TODO remove

    async function updateActiveCustomerByParams() {
      if (!to.params.customerId) {
        clearActiveCustomer()
        return
      }
      return setActiveCustomer(to.params.customerId as string)
    }

    async function updateCurrentUser() {
      if (user.value) {
        return
      }

      const data = await getMe()
      /* If the user is a tenant, load the tenant scopes once here
         so we don't need to keep checking if they're loaded and load
         them later.. */
      if (data.type === UserType.TENANT) {
        await useTenantScopesStore().loadTenantScopes()
      }
      return data
    }

    async function clearRedirect() {
      if (preAuthRedirect) {
        await localforage.removeItem(redirectValueKey)
      }
    }

    try {
      if (!tokenExpirationTimestamp || Date.now() >= tokenExpirationTimestamp) {
        await checkOAuthSession()
      }

      await Promise.all([
        updateActiveCustomerByParams(),
        updateCurrentUser(),
      ])

      if (to.meta.permission) {
        if ((to.meta.permission.scope && !hasScope(to.meta.permission.scope)) ||
            (to.meta.permission.minType && !hasMinType(to.meta.permission.minType))) {
          if (typeof to.meta.permission?.redirect === 'function') {
            return await to.meta.permission?.redirect()
          }

          const customerId = useCustomerId()

          return to.meta.permission?.redirect ?? {
            name: constants.ROUTES_MODULES,
            params: { customerId },
          }
        }

        return true
      }

      if (preAuthRedirect) {
        /* We have a pre-auth redirect URL with potential parameters
           stored that we want to navigate to, post-auth. First, clear
           it from persistent local storage. Second, return it as
           a path so the router will navigate to it. Decode query params
           into an object to pass as query or we'll loose those. */
        await clearRedirect()
        let queryObject = {}
        if (preAuthRedirect.includes('?')) {
          const urlParams = new URLSearchParams(preAuthRedirect.split('?')[1])
          queryObject = Object.fromEntries(urlParams)
        }
        return {
          path: preAuthRedirect,
          query: queryObject,
        }
      }
    } catch {
      /* Prevent navigation. */
      return false
    }

    /* Allow navigation. */
    return true
  }
}
